import React, { lazy, Suspense } from 'react';
import CourseList from '../components/course/CourseList';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import teacherImage from '../assets/images/teachers/hossam.png';
import CodeEntryForm from '../components/common/CodeEntryForm';
import BooksList from '../components/common/BooksList';
import { BookOpen, Trophy } from 'lucide-react';
// Import floating icons
import chalkboardIcon from '../assets/images/icons/chalkboard.png';
import geometryIcon from '../assets/images/icons/geometry.png';
import mathIcon from '../assets/images/icons/math.png';
import mathematicsIcon from '../assets/images/icons/mathematics.png';
import toolsIcon from '../assets/images/icons/tools.png';
const GradeCards = lazy(() => import('../components/common').then(module => ({ default: module.GradeCards })));
const TopStudents = lazy(() => import('../components/common/TopStudents'));


interface Stat {
  number: string;
  label: string;
}

// Generic method to create floating math icons
const createFloatingIcons = (numIcons: number, opacity: number = 0.2) => {
  const icons = [chalkboardIcon, geometryIcon, mathIcon, mathematicsIcon, toolsIcon];
  const positions = [
    { top: '5%', left: '5%' },
    { top: '10%', right: '10%' },
    { top: '15%', left: '20%' },
    { top: '20%', right: '25%' },
    { top: '25%', left: '15%' },
    { top: '30%', right: '15%' },
    { top: '35%', left: '30%' },
    { top: '40%', right: '20%' },
    { top: '45%', left: '10%' },
    { top: '50%', right: '30%' },
    { top: '55%', left: '25%' },
    { top: '60%', right: '15%' },
    { top: '65%', left: '20%' },
    { top: '70%', right: '25%' },
    { top: '75%', left: '15%' },
    { top: '80%', right: '20%' },
    { bottom: '10%', left: '30%' },
    { bottom: '15%', right: '15%' },
    { bottom: '20%', left: '25%' },
    { bottom: '25%', right: '30%' },
  ];

  const floatingIcons = [];
  for (let i = 0; i < numIcons && i < positions.length; i++) {
    const iconSrc = icons[i % icons.length];
    const position = positions[i];
    const delay = (i * 0.5).toFixed(1);

    floatingIcons.push(
      <img
        key={`floating-icon-${i}`}
        src={iconSrc}
        alt=""
        className="absolute w-12 h-12 animate-float"
        style={{
          ...position,
          opacity: opacity,
          animationDelay: `${delay}s`,
        }}
      />
    );
  }

  return floatingIcons;
};

// Optimized floating icons - reduced count and simplified animations
const createOptimizedIcons = (section: string = 'default') => {
  const icons = [
    // Reduced from 21 to 8 icons for better performance
    { src: 'anubis', top: '5%', left: '5%', size: '35px', delay: 0 },
    { src: 'hieroglyph', top: '8%', left: '25%', size: '40px', delay: 0.5 },
    { src: 'pharaoh', top: '12%', left: '45%', size: '45px', delay: 1 },
    { src: 'globe', top: '6%', left: '65%', size: '38px', delay: 1.5 },
    { src: 'world', top: '15%', left: '85%', size: '60px', delay: 2 },
    { src: 'anubis', top: '45%', left: '12%', size: '34px', delay: 2.5 },
    { src: 'hieroglyph', top: '65%', left: '52%', size: '41px', delay: 3 },
    { src: 'pharaoh', top: '85%', left: '38%', size: '44px', delay: 3.5 },
  ];

  return icons.map((icon, index) => (
    <div
      key={`${section}-icon-${index}`}
      className="absolute pointer-events-none z-10 opacity-15"
      style={{
        top: icon.top,
        left: icon.left,
        width: icon.size,
        height: icon.size,
        animationDelay: `${icon.delay}s`,
        animationDuration: '4s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'ease-in-out',
        willChange: 'transform'
      }}
    >
      <div className="w-full h-full bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full animate-pulse"></div>
    </div>
  ));
};

// Memoized section component
const PremiumSection = React.memo(({
  children,
  title,
  badgeText,
  icon: Icon,
  id
}: {
  children: React.ReactNode;
  title: string;
  badgeText: string;
  icon: React.ComponentType<any>;
  id?: string;
}) => (
  <section id={id} className="py-6 sm:py-12 lg:py-16 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 relative overflow-hidden">
    {/* Simplified decorative borders */}
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>

    {/* Simplified background elements */}
    <div className="absolute inset-0">
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-2xl"></div>
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-purple-400/10 to-blue-400/10 rounded-full blur-2xl"></div>
    </div>

    {/* Floating icons */}
    <div className="absolute inset-0 pointer-events-none z-10">
      {createOptimizedIcons(title.toLowerCase())}
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      {/* Merged header and content container */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
        {/* Header section at the top */}


        {/* Content section below header */}
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="relative z-10">
            {children}
          </div>
        </div>
      </div>
    </div>
  </section>
));

PremiumSection.displayName = 'PremiumSection';

const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();

  const scrollToCourses = () => {
    const coursesSection = document.getElementById('courses-section');
    if (coursesSection) {
      coursesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const stats: Stat[] = [
    { number: '4000+', label: 'طالب مشترك' },
    { number: '99+', label: 'فيديو تعليمي' },
    { number: '24 ساعة', label: 'دعم متواصل' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-primary-light/5 to-white" dir="rtl">
      {/* Hero Section */}
      <section className="relative min-h-screen md:h-screen w-full overflow-hidden bg-white pb-8 md:pb-0">
        {/* Floating Math Icons */}
        <div className="absolute inset-0 pointer-events-none z-0">
          {createFloatingIcons(15, 0.07)}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full pt-20 md:pt-0 pb-8 md:pb-0">
          <div className="flex flex-col md:flex-row h-full w-full items-center">

            {/* Content Side with Atom Animation */}
            <div className="md:basis-1/2 w-full flex justify-center items-center relative py-10 md:py-0">
              {/* Atom Animation */}
             
              <div className="relative z-10 flex flex-col items-center md:items-start space-y-6 text-center md:text-right">
                <div className="flex flex-col items-center md:items-start space-y-3">
                  <h1 className="font-el-messiri font-bold text-5xl md:text-7xl leading-tight pt-2 pb-4 bg-gradient-to-l from-primary to-primary-dark bg-clip-text text-transparent">
                    منصة م/ حسام رسلان<br />
                  </h1>
                  <div className="w-1/2 h-[1px] bg-primary-dark" />
                </div>

                <p className="font-cairo text-lg md:text-2xl text-slate-600 pt-4 max-w-lg">
                  طالما عندك هدف تقدر توصل لأبعد مما تتخيل<br />
                  واحنا نقدر نكون شركاء هدفك ونجاحك!
                </p>

                <div className="pt-8 md:pt-14">
                  <button
                    onClick={scrollToCourses}
                    className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 text-xl font-bold text-white bg-gradient-to-r from-primary via-primary to-primary-dark rounded-2xl shadow-lg shadow-primary/50 hover:shadow-xl hover:shadow-primary/60 transition-all duration-300 hover:scale-105 active:scale-95 overflow-hidden"
                  >
                    {/* Animated background gradient */}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-dark via-primary to-primary-dark opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Shine effect */}
                    <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
                    
                    {/* Content */}
                    <div className="relative z-10 flex items-center gap-3">
                      <svg
                        className="h-7 w-7 group-hover:rotate-12 transition-transform duration-300"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                      </svg>
                      <span className="group-hover:translate-x-[-2px] transition-transform duration-300">النجاح بيبدأ هنا!</span>
                      <svg
                        className="h-5 w-5 group-hover:translate-x-[-4px] transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                      </svg>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Image Side */}
            <div className="md:basis-1/2 w-full flex justify-center items-center">
              <div className="relative max-w-lg md:max-w-2xl w-full px-10 sm:px-0">
                <img
                  src={teacherImage}
                  alt="المعلم حسام رسلان"
                  className="w-full h-auto"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-8 sm:py-16 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block">
              <h2 className="font-el-messiri text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-4">
                مميزات <span className="text-primary">منصتنا</span>
              </h2>
              <div className="h-1 bg-gradient-to-r from-transparent via-primary to-transparent"></div>
            </div>
            <p className="font-cairo text-lg sm:text-xl text-slate-600 mt-6 max-w-2xl mx-auto">
              تفتكر ليه م/ <span className="font-bold text-primary-dark">حسام رسلان</span> ؟
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Feature Card 1 */}
            <div className="flex flex-col justify-center w-full h-auto text-center p-6 bg-sky-100 space-y-4 text-black shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-colors duration-300 rounded-xl hover:bg-sky-200">
              <div className="flex flex-col-reverse items-center justify-center">
                <h3 className="font-el-messiri text-2xl font-bold text-slate-900">تعويض ال فات بسهولة</h3>
                <span className="mb-4">
                  <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
              </div>
              <p className="font-cairo text-center w-full text-slate-700 text-sm">
                لو فاتك أي محاضرة، تقدر تشوفها في أي وقت تريده. كل المحاضرات متاحة 24/7 علشان ما تفوتش حاجة
              </p>
            </div>

            {/* Feature Card 2 */}
            <div className="flex flex-col justify-center w-full h-auto text-center p-6 bg-sky-100 space-y-4 text-black shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-colors duration-300 rounded-xl hover:bg-sky-200">
              <div className="flex flex-col-reverse items-center justify-center">
                <h3 className="font-el-messiri text-2xl font-bold text-slate-900">متابعة مستمرة لكورساتك</h3>
                <span className="mb-4">
                  <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                  </svg>
                </span>
              </div>
              <p className="font-cairo text-center w-full text-slate-700 text-sm">
                وفرنالك dashboard كـ طالب تقدر من خلالها تتابع مستواك، نتايجك، وكورساتك. كل حاجة في مكان واحد
              </p>
            </div>

            {/* Feature Card 3 */}
            <div className="flex flex-col justify-center w-full h-auto text-center p-6 bg-sky-100 space-y-4 text-black shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-colors duration-300 rounded-xl hover:bg-sky-200">
              <div className="flex flex-col-reverse items-center justify-center">
                <h3 className="font-el-messiri text-2xl font-bold text-slate-900">شرح واضح + امثلة محلولة</h3>
                <span className="mb-4">
                  <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                </span>
              </div>
              <p className="font-cairo text-center w-full text-slate-700 text-sm">
                شرح مبسط وواضح لكل درس مع أمثلة محلولة خطوة بخطوة. كل حاجة هتكون واضحة ومفهومة
              </p>
            </div>

            {/* Feature Card 4 */}
            <div className="flex flex-col justify-center w-full h-auto text-center p-6 bg-sky-100 space-y-4 text-black shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-colors duration-300 rounded-xl hover:bg-sky-200">
              <div className="flex flex-col-reverse items-center justify-center">
                <h3 className="font-el-messiri text-2xl font-bold text-slate-900">أسئلة وامتحانات تقيم بها مستواك</h3>
                <span className="mb-4">
                  <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                  </svg>
                </span>
              </div>
              <p className="font-cairo text-center w-full text-slate-700 text-sm">
                أسئلة تفاعلية وامتحانات إلكترونية بتتصحح تلقائي. تقدر تعرف مستواك في أي وقت وتتابع تطورك
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* YouTube Player Section */}
    

      {/* Grade Selection Section */}
      <PremiumSection id="courses-section" title="" badgeText="" icon={BookOpen}>
        <Suspense fallback={<div className="text-center py-6">جاري التحميل...</div>}>
          <GradeCards />
        </Suspense>
      </PremiumSection>

      {/* Books Section - same API with our tenant ID via api interceptor */}
      <div id="books-section" className="py-16 sm:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-sky-100 to-primary/10 backdrop-blur-md px-6 py-3 rounded-full border border-sky-200/50 mb-6">
              <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 2H8c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H8V4h11v16zM6 6H4v16c0 1.1.9 2 2 2h12v-2H6V6z"/>
              </svg>
              <span className="text-primary-dark font-medium">كتبنا المميزة</span>
            </div>
            <h2 className="font-el-messiri text-4xl lg:text-5xl font-bold text-slate-800 mb-4 flex items-center justify-center gap-4 flex-wrap">
              <svg className="w-10 h-10 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 2H8c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H8V4h11v16zM6 6H4v16c0 1.1.9 2 2 2h12v-2H6V6z"/>
              </svg>
              <span>كتبنا <span className="text-primary bg-clip-text">المميزة</span></span>
            </h2>
            <p className="font-cairo text-lg text-slate-600 max-w-2xl mx-auto">
              تصفح الكتب المتاحة لمرحلتك الدراسية
            </p>
          </div>
          <BooksList />
        </div>
      </div>

      {/* لوحة الشرف — top students (same API as pointing / leaderboard) */}
      <section
        id="leaderboard-section"
        className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-sky-50/80 via-primary-light/30 to-white overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
          <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-gradient-to-br from-primary to-primary-dark rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-sky-400 to-primary/40 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Suspense
            fallback={
              <div className="text-center py-12">
                <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-100 to-amber-100 px-6 py-3 rounded-full mb-8 font-cairo">
                  <Trophy className="w-5 h-5 text-yellow-600 animate-bounce" />
                  <span className="text-yellow-800 font-semibold">جاري تحميل قائمة المتفوقين...</span>
                </div>
              </div>
            }
          >
            <TopStudents />
          </Suspense>
        </div>
      </section>

      {/* Code Entry Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CodeEntryForm />
        </div>
      </div>

      {/* Floating Sticky Button - Only show for non-authenticated users */}
      {!isAuthenticated && (
        <Link
          to="/register"
          className="fixed bottom-6 left-6 z-40 bg-gradient-to-r from-primary to-primary-dark hover:from-primary-dark hover:to-primary text-white font-bold py-3 px-6 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 font-cairo text-sm flex items-center gap-2 group"
          style={{
            animation: 'bounce 2s infinite',
          }}
        >
          <svg className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>سجل الآن</span>

          {/* Pulse effect */}
          <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-20"></div>
        </Link>
      )}

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        @keyframes move {
          100% { transform: translate(100%, 100%); }
        }

        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          50% { 
            transform: translateY(-20px) rotate(5deg); 
          }
        }

        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes image-reveal {
          0% { 
            opacity: 0;
            transform: translateY(20px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-down {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        @keyframes slide-right {
          from { transform: translateX(-20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        @keyframes slide-up {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float-delayed 3s ease-in-out infinite, fade-in 0.5s ease-out forwards;
          animation-delay: 1.5s;
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }

        .animate-image-reveal {
          animation: image-reveal 1s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }

        .animate-slide-down {
          animation: slide-down 0.8s ease-out forwards;
        }

        .animate-slide-right {
          animation: slide-right 0.8s ease-out forwards;
        }

        .animate-slide-up {
          animation: slide-up 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Home;